import { DatePicker, Label } from '@/components/basic';
import AnchorDayPicker, { AnchorDaysObject } from '@/components/basic/DatePicker/AnchorDayPicker';
import FormErrorMessage from '@/components/basic/FormMessages/FormErrorMessage';
import {
  defaultValues,
  useCreatePermissionProvider
} from '@/components/permissions/CreatePermission/CreatePermissionProvider';
import TeamsPermissionList from '@/components/permissions/TeamsPermissionList';
import UsersPermissionList from '@/components/permissions/UsersPermissionList';
import { classNames } from '@/lib/classNames';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Checkbox } from 'reakit';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@v2/components/ui/tabs';
import { addMonths } from 'date-fns';
import { useTabState } from 'reakit/Tab';
import { useIsDesignSystem2 } from '@gettactic/hooks';
const defaultAnchorDays: AnchorDaysObject = {
  sunday: false,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false
};

export default function CreatePermissionForm({isOpen}: {isOpen: boolean}) {
  const { submit, form } = useCreatePermissionProvider();

  const permanentAssignment = form.watch('permanentAssignment');
  const noExpirationDate = form.watch('noExpirationDate');
  const anchorDays = form.watch('anchorDays');
  const since = form.watch('since');
  const until = form.watch('until');
  
  const tab = useTabState();

  const today = new Date();

  const currentDayIdx = today.getDay();

  const minMaxDates = useMemo(() => {
    const permanentOrNoExpirationDate = permanentAssignment || noExpirationDate;
    const minDate = permanentOrNoExpirationDate ? undefined : (since || today);
    
    let maxDate: Date | undefined = permanentOrNoExpirationDate ? undefined : (until || undefined);

    if (!permanentOrNoExpirationDate && !maxDate) {
      maxDate = addMonths(today, 3);
    }

    return { minDate, maxDate };
  }, [since, until, permanentAssignment, noExpirationDate]);

  React.useEffect(() => {
    if (permanentAssignment) {
      form.setValue('anchorDays', defaultAnchorDays);
    }
  }, [permanentAssignment]);

  React.useEffect(() => {
    if (noExpirationDate || permanentAssignment) {
      form.setValue('until', null);
    }
    if (!permanentAssignment && !noExpirationDate) {
      form.setValue('until', defaultValues.until);
    }
    
    // When these constraints change, ensure the component re-renders
    // This allows the AnchorDayPicker to update its disabled states
    form.trigger('anchorDays');
  }, [noExpirationDate, permanentAssignment, defaultValues.until, form]);

  React.useEffect(() => {
    if (!isOpen) {
      // to reset on modal close
      tab.setSelectedId('users');
    }
  }, [isOpen]);

  // Initialize anchorDays with default values if not already set
  React.useEffect(() => {
    if (!anchorDays) {
      form.setValue('anchorDays', defaultAnchorDays);
    }
  }, []);

  const users = form.watch('users', []);
  const teams = form.watch('teams', []);

  const tabs: {
    id: string;
    title: string;
    render: () => JSX.Element | null;
  }[] = [
    {
      id: 'users',
      title: `Users (${users.length})`,
      render: () => (
        <UsersPermissionList
          ids={users}
          add={(id: string) => {
            form.setValue('users', [...users, id]);
          }}
          remove={(id: string) => {
            form.setValue(
              'users',
              users.filter((x) => x !== id)
            );
          }}
        />
      )
    },
    {
      id: 'teams',
      title: `Teams (${teams.length})`,
      render: () => (
        <TeamsPermissionList
          ids={teams}
          add={(id: string) => {
            form.setValue('teams', [...teams, id]);
          }}
          remove={(id: string) => {
            form.setValue(
              'teams',
              teams.filter((x) => x !== id)
            );
          }}
        />
      )
    }
  ];

  const isV2 = useIsDesignSystem2();


  return (
    <form
      id="createOrEditHourRule"
      className="flex flex-auto flex-col flex-nowrap overflow-hidden"
      onSubmit={form.handleSubmit(submit)}
    >
      <div>
        <div className="flex flex-auto flex-col overflow-y-auto">
          <div className="mt-4 grid grid-cols-12 gap-x-6 gap-y-3">
            <div className="col-span-12">
              <div className="col-span-12 ">
                <div className="col-span-12 flex justify-between space-x-4 px-1">
                  <div className="flex w-full items-center">
                    <Checkbox
                      id="enableRestrictions"
                      checked={permanentAssignment}
                      className="rounded text-primary focus:ring-0"
                      {...form.register('permanentAssignment')}
                    />
                    <label
                      htmlFor="enableRestrictions"
                      className="ml-2 font-medium text-base text-gray-800"
                    >
                      Permanent Assignment
                    </label>
                  </div>
                  <div className="flex w-full items-center">
                    {!permanentAssignment ? (
                      <>
                        <Checkbox
                          className="rounded text-primary focus:ring-0"
                          id="noExpirationDate"
                          checked={noExpirationDate}
                          {...form.register('noExpirationDate')}
                        />
                        <label
                          htmlFor="noExpirationDate"
                          className="ml-2 font-medium text-base text-gray-800"
                        >
                          No Expiration Date
                        </label>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={classNames(
                'col-span-12',
                permanentAssignment && 'hidden'
              )}
            >
              <div className="col-span-12 mb-2 ">
                <div className="col-span-12 flex justify-between space-x-4 px-1">
                  <div className="w-full">
                    <Label htmlFor="since" className="block text-gray-800">
                      Start Date
                    </Label>
                    <Controller
                      control={form.control}
                      rules={{ required: !permanentAssignment }}
                      name="since"
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          selected={value}
                          name="since"
                          className="input w-full rounded-md"
                          calendarClassName="tailwind-datepicker"
                          timeCaption="Since"
                          onChange={onChange}
                        />
                      )}
                    />
                  </div>
                  <div className="w-full">
                    <div className={classNames(noExpirationDate && 'hidden')}>
                      <Label htmlFor="until" className="block">
                        End Date
                      </Label>
                      <Controller
                        control={form.control}
                        rules={{
                          required: !permanentAssignment && !noExpirationDate
                        }}
                        name="until"
                        render={({ field: { onChange, value } }) => (
                          <DatePicker
                            selected={value}
                            name="until"
                            className="input w-full rounded-md"
                            calendarClassName="tailwind-datepicker"
                            onChange={onChange}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-span-12">
                  <FormErrorMessage
                    errors={form.formState.errors}
                    fieldName={'since'}
                  />
                  <FormErrorMessage
                    errors={form.formState.errors}
                    fieldName={'until'}
                  />
                </div>
              </div>
            </div>
            {isV2 && !permanentAssignment && <div className="col-span-12">
              <label className="block text-gray-800" htmlFor="anchorDays">
                Anchor Days
              </label>

              <AnchorDayPicker
                form={form}
                anchorDays={anchorDays}
                currentDayIdx={currentDayIdx}
                singleSelect={false}
                name="anchorDays"
                minDate={minMaxDates.minDate}
                maxDate={minMaxDates.maxDate}
              />

              <FormErrorMessage
                errors={form.formState.errors}
                fieldName={'anchorDays'}
              />
            </div>}
            <Tabs
              defaultValue={tabs[0]?.id || ''}
              className="col-span-12"
            >
              <TabsList
                aria-label="Permission tabs"
                className='col-span-12 flex flex-none gap-8 border-gray-200 border-b text-base'
                >
                {tabs.map((tabDef) => (
                  <TabsTrigger
                    key={tabDef.id}
                    value={tabDef.id}
                    variant="minimalistic"
                    type="button"
                  >
                    {tabDef.title}
                  </TabsTrigger>
                ))}
              </TabsList>
              {tabs.map((tabDef) => (
                <TabsContent
                  key={tabDef.id}
                  value={tabDef.id}

                >
                  {tabDef.render()}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
    </form>
  );
}
